import { Button, Modal, Select, TextInput, Textarea } from 'flowbite-react';
import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import Loader from '../Common/Loader';
import { useNavigate } from 'react-router-dom';
import UserApi from '../../Api/UserApi';
import { User } from '../../Models/User';
import {
  fetchUserLevels,
  fetchExistingEmails,
} from '../../Actions/CommonFields';
import { useAppDispatch, useAppSelector } from '../../Reducers/store';
import { UserLevel, UserLevelNames } from '../../Models/UserLevel';
import { HiInformationCircle } from 'react-icons/hi';

export default function AddUserModal({
  show,
  setShow,
}: {
  show: boolean;
  setShow: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { userLevels, existingEmails } = useAppSelector(
    (state) => state.commonFields
  );
  const { userModel } = useAppSelector((state) => state.user);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [buttonDisabled, setButtonDisabled] = useState<boolean>(false);
  const [emailExists, setEmailExists] = useState<boolean>(false);
  const [formData, setFormData] = useState<User>({
    id: 0,
    firstName: '',
    lastName: '',
    emailAddress: '',
    phoneNumber: '',
    birthdate: null,
    userLevelId: 1,
    isPrimary: false,
    address: '',
    city: '',
    state: '',
    zip: '',
    bio: '',
    clientId: null,
    emailConfirmed: null,

    client: null,
    userLevel: null,
  });

  const getReferences = async () => {
    if (!userLevels.length || !existingEmails.length) {
      await Promise.all([
        dispatch(fetchUserLevels()),
        dispatch(fetchExistingEmails()),
      ]);
      //   setIsLoading(false);
    } else {
      //   setIsLoading(false);
    }
  };

  useEffect(() => {
    if (show === true) {
      getReferences();
    }
  }, [show]);

  const checkEmail = (email: string) => {
    if (existingEmails.some((x) => x === email.trim().toLowerCase())) {
      setButtonDisabled(true);
      setEmailExists(true);
    } else {
      setButtonDisabled(false);
      setEmailExists(false);
    }
  };

  const handleChangeInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
    if (name === 'emailAddress') {
      checkEmail(value);
    }
  };

  const handleChangeTextArea = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleChangeSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const onSubmit = async (e: React.ChangeEvent<HTMLFormElement>) => {
    setButtonDisabled(true);
    e.preventDefault();
    try {
      setIsLoading(true);
      return UserApi.addUpdateUser(formData)
        .then((res) => {
          if (res.data.success) {
            toast.success('success');
            setIsLoading(false);
            setButtonDisabled(false);
            navigate(`/user/${res.data.affectedEntityIdentifier}`);
          } else {
            toast.error(res.data.message);
            setIsLoading(false);
            setButtonDisabled(false);
          }
        })
        .catch((err) => {
          toast.error('Error: ' + err);
          setIsLoading(false);
          setButtonDisabled(false);
        });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      setButtonDisabled(false);
    }
  };
  function onCloseModal() {
    setShow(false);
  }
  return (
    <Modal show={show} size='4xl' onClose={onCloseModal} popup>
      <Modal.Header></Modal.Header>
      <Modal.Body>
        <div className='w-full'>
          <div className='w-full'>
            <div className='mx-1 flex items-end justify-center'>
              <div className='flex flex-1 justify-center'>
                <h1 className='text-gray-900 font-bold text-4xl'>Add User</h1>
              </div>
            </div>
          </div>

          <div>
            <div className='container mx-auto p-2'>
              <div className='md:flex no-wrap md:-mx-2 '>
                <div className='w-full'>
                  <div className='bg-white'>
                    <div className='flex items-center space-x-2 font-bold text-gray-500 leading-8'>
                      <span className='tracking-wide uppercase text-3xl'>
                        User Information
                      </span>
                    </div>
                    <hr className='bg-gray-300 h-[5px]'></hr>
                    <form onSubmit={onSubmit}>
                      <div className='text-gray-500'>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Name:
                            </div>
                          </div>
                          <div className='flex'>
                            <div className='w-1/3'>
                              <TextInput
                                id='firstName'
                                name='firstName'
                                type='text'
                                placeholder='First Name...'
                                onChange={handleChangeInput}
                                value={formData.firstName ?? ''}
                                sizing='md'
                              />
                            </div>
                            <div className='w-1/3'>
                              <TextInput
                                id='lastName'
                                name='lastName'
                                type='text'
                                placeholder='Last Name...'
                                onChange={handleChangeInput}
                                value={formData.lastName ?? ''}
                                sizing='md'
                              />
                            </div>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Email:
                            </div>
                          </div>
                          <div className='flex'>
                            <div className='w-1/3'>
                              <TextInput
                                id='emailAddress'
                                name='emailAddress'
                                type='text'
                                placeholder='Email Address...'
                                onChange={handleChangeInput}
                                value={formData.emailAddress ?? ''}
                                sizing='md'
                                required
                              />
                            </div>
                            {emailExists && (
                              <div className='flex w-2/3 text-red-600'>
                                <div className='content-center px-2'>
                                  <HiInformationCircle />
                                </div>
                                <div className='content-center'>
                                  Email already exists
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              Phone:
                            </div>
                          </div>
                          <div className='w-1/3'>
                            <TextInput
                              id='phoneNumber'
                              name='phoneNumber'
                              type='text'
                              placeholder='Phone...'
                              onChange={handleChangeInput}
                              value={formData.phoneNumber ?? ''}
                              sizing='md'
                            />
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>
                              User Level:
                            </div>
                          </div>
                          <div className='w-1/3'>
                            <Select
                              id='userLevelId'
                              name='userLevelId'
                              onChange={handleChangeSelect}
                              value={formData.userLevelId ?? 2}
                              sizing='md'
                            >
                              {userLevels.map((userLevel) => (
                                <option key={userLevel.id} value={userLevel.id}>
                                  {userLevel.name}
                                </option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        <hr className='bg-gray-300 h-[2px]'></hr>
                        <div className='grid grid-cols-[20%_80%] text-xl'>
                          <div className='flex'>
                            <div className='font-bold content-center'>Bio:</div>
                          </div>
                          <div>
                            <Textarea
                              id='bio'
                              name='bio'
                              placeholder='Bio...'
                              onChange={handleChangeTextArea}
                              value={formData.bio ?? ''}
                              rows={4}
                            />
                          </div>
                        </div>
                      </div>
                      <div className='flex justify-end pt-2'>
                        <Button
                          disabled={buttonDisabled}
                          type='submit'
                          className='buttonMain'
                        >
                          Save
                        </Button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
